import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  TextField,
} from '@mui/material';
import {
  AddCircleOutline as SeedIcon,
  Refresh as RefreshIcon,
  AutoAwesome as GenerateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getLibraryStats,
  generateSeedRecipes,
  batchSeedRecipes,
  getSeedingStatus,
  LibraryStat,
  SeedingPreview,
  SeedingResult,
} from '../adminApi';

const DIETARY_TAG_GROUPS: { label: string; tags: string[] }[] = [
  {
    label: 'Restrictions',
    tags: ['vegetarian', 'vegan', 'pescatarian', 'halal', 'kosher', 'no-red-meat', 'no-pork'],
  },
  {
    label: 'Allergies',
    tags: ['nut-free', 'dairy-free', 'gluten-free', 'egg-free', 'soy-free', 'sesame-free', 'shellfish-free'],
  },
  {
    label: 'Goals & Medical',
    tags: [
      'low-carb',
      'low-sodium',
      'low-sugar',
      'low-fat',
      'heart-healthy',
      'diabetic-friendly',
      'kidney-friendly',
      'celiac',
    ],
  },
];

const CUISINE_OPTIONS = [
  'American', 'Italian', 'Mexican', 'Indian', 'Chinese', 'Japanese', 'Thai',
  'Mediterranean', 'Korean', 'Middle Eastern', 'French', 'Greek', 'Vietnamese',
  'Ethiopian', 'Caribbean', 'German', 'Spanish', 'Brazilian', 'British', 'Moroccan',
];

const RecipeSeeding: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<LibraryStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Generate form state
  const [mealType, setMealType] = useState<string>('dinner');
  const [dietaryTags, setDietaryTags] = useState<string[]>([]);
  const [kidFriendly, setKidFriendly] = useState(false);
  const [quickEasy, setQuickEasy] = useState(false);
  const [isModular, setIsModular] = useState(false);
  const [maxTotalTime, setMaxPrepTime] = useState(0);
  const [cuisine, setCuisine] = useState<string>('');
  const [requiredIngredient, setRequiredIngredient] = useState<string>('');
  const [count, setCount] = useState(20);
  const [generating, setGenerating] = useState(false);

  // Preview state
  const [preview, setPreview] = useState<SeedingPreview[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Add-by-name state — for when admin knows exactly which recipes to add
  // (e.g. user requests "PB&J, mac and cheese") and wants to skip the AI
  // name-generation step. Names are converted directly into stubs.
  const [byNameInput, setByNameInput] = useState('');
  const [byNameMealType, setByNameMealType] = useState<string>('dinner');
  const [byNameTags, setByNameTags] = useState<string[]>([]);

  // Batch seeding state
  const [seeding, setSeeding] = useState(false);
  const [seedProgress, setSeedProgress] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SeedingResult | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLibraryStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load library stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);
    setPreview([]);
    setLastResult(null);

    try {
      const recipes = await generateSeedRecipes({
        meal_type: mealType,
        dietary_tags: dietaryTags.length > 0 ? dietaryTags : undefined,
        count,
        kid_friendly: kidFriendly || undefined,
        cuisine: cuisine || undefined,
        required_ingredient: requiredIngredient.trim() || undefined,
        max_total_time: maxTotalTime || undefined,
        quick_easy: quickEasy || undefined,
        is_modular: isModular || undefined,
      });
      setPreview(recipes);
      setSelected(new Set(recipes.map((_, i) => i)));
    } catch (err: any) {
      setError(err.message || 'Failed to generate recipes');
    } finally {
      setGenerating(false);
    }
  };

  const handleSeed = async () => {
    const selectedRecipes = preview.filter((_, i) => selected.has(i));
    if (selectedRecipes.length === 0) return;

    setSeeding(true);
    setSeedProgress('Starting...');
    setError(null);
    setSuccess(null);
    setLastResult(null);

    try {
      const { jobId } = await batchSeedRecipes(selectedRecipes, mealType, isModular || undefined);

      const poll = async () => {
        try {
          const status = await getSeedingStatus(jobId);
          setSeedProgress(
            `Creating ${status.progress || 0}/${status.total || selectedRecipes.length} recipes...`
          );

          if (status.status === 'done') {
            setLastResult(status.results);
            setSuccess(
              `Seeding complete: ${status.results.created} created, ${status.results.skipped || 0} skipped (duplicates), ${status.results.failed} failed`
            );
            setSeeding(false);
            setSeedProgress(null);
            setPreview([]);
            await fetchStats();
          } else if (status.status === 'error') {
            setError(status.error || 'Seeding failed');
            setSeeding(false);
            setSeedProgress(null);
          } else {
            setTimeout(poll, 2000);
          }
        } catch (err: any) {
          setError(err.message || 'Failed to check seeding status');
          setSeeding(false);
          setSeedProgress(null);
        }
      };

      setTimeout(poll, 1500);
    } catch (err: any) {
      setError(err.message || 'Batch seeding failed');
      setSeeding(false);
      setSeedProgress(null);
    }
  };

  const handleAddByName = async () => {
    const names = byNameInput
      .split('\n')
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    if (names.length === 0) {
      setError('Enter at least one recipe name (one per line).');
      return;
    }

    const recipes: SeedingPreview[] = names.map((name) => ({
      name,
      description: '',
      total_time: 30,
      dietary_tags: byNameTags,
    }));

    setSeeding(true);
    setSeedProgress('Starting...');
    setError(null);
    setSuccess(null);
    setLastResult(null);
    setPreview([]);

    try {
      const { jobId } = await batchSeedRecipes(recipes, byNameMealType);

      const poll = async () => {
        try {
          const status = await getSeedingStatus(jobId);
          setSeedProgress(
            `Creating ${status.progress || 0}/${status.total || recipes.length} recipes...`
          );

          if (status.status === 'done') {
            setLastResult(status.results);
            setSuccess(
              `Added ${status.results.created} recipes (${status.results.skipped || 0} duplicates skipped, ${status.results.failed} failed)`
            );
            setSeeding(false);
            setSeedProgress(null);
            setByNameInput('');
            await fetchStats();
          } else if (status.status === 'error') {
            setError(status.error || 'Seeding failed');
            setSeeding(false);
            setSeedProgress(null);
          } else {
            setTimeout(poll, 2000);
          }
        } catch (err: any) {
          setError(err.message || 'Failed to check seeding status');
          setSeeding(false);
          setSeedProgress(null);
        }
      };

      setTimeout(poll, 1500);
    } catch (err: any) {
      setError(err.message || 'Add by name failed');
      setSeeding(false);
      setSeedProgress(null);
    }
  };

  const toggleSelected = (index: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === preview.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(preview.map((_, i) => i)));
    }
  };

  if (loading && stats.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Recipe Seeding
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI-generate recipe stubs to grow the library, then enrich them with ingredients & steps
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchStats}
          disabled={loading || generating || seeding}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Library Stats */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))"
        gap={2}
        mb={3}
      >
        {stats.map((s) => (
          <Paper key={s.meal_type} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold" textTransform="capitalize">
              {s.meal_type}
            </Typography>
            <Typography
              variant="h4"
              fontWeight="bold"
              color={s.complete < 30 ? 'error.main' : 'success.main'}
            >
              {s.complete}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              complete / {s.total} total
            </Typography>
          </Paper>
        ))}
        {stats.length === 0 && (
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">No recipes with meal_type set</Typography>
          </Paper>
        )}
      </Box>

      {/* Generate Panel */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Generate Recipe Names
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Use AI to suggest new recipe names. Review the preview, then seed selected recipes as stubs.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap' }} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Meal Type</InputLabel>
            <Select
              value={mealType}
              label="Meal Type"
              onChange={(e) => setMealType(e.target.value)}
            >
              <MenuItem value="breakfast">Breakfast</MenuItem>
              <MenuItem value="lunch">Lunch</MenuItem>
              <MenuItem value="dinner">Dinner</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Max Total Time</InputLabel>
            <Select
              value={maxTotalTime}
              label="Max Total Time"
              onChange={(e) => setMaxPrepTime(Number(e.target.value))}
            >
              <MenuItem value={0}>Default</MenuItem>
              <MenuItem value={10}>10 min</MenuItem>
              <MenuItem value={15}>15 min</MenuItem>
              <MenuItem value={20}>20 min</MenuItem>
              <MenuItem value={30}>30 min</MenuItem>
              <MenuItem value={45}>45 min</MenuItem>
              <MenuItem value={60}>60 min</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Dietary Tags</InputLabel>
            <Select
              multiple
              value={dietaryTags}
              label="Dietary Tags"
              onChange={(e) => setDietaryTags(e.target.value as string[])}
              renderValue={(sel) => sel.join(', ')}
            >
              {DIETARY_TAG_GROUPS.map((group) => [
                <MenuItem key={group.label} disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', mt: 0.5 }}>
                  {group.label}
                </MenuItem>,
                ...group.tags.map((tag) => (
                  <MenuItem key={tag} value={tag} sx={{ pl: 3 }}>
                    <Checkbox checked={dietaryTags.includes(tag)} size="small" />
                    {tag}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Cuisine</InputLabel>
            <Select
              value={cuisine}
              label="Cuisine"
              onChange={(e) => setCuisine(e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {CUISINE_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Optional ingredient constraint — admins can target specific ingredients
              like "haddock" or "ground beef" when users request more variety. */}
          <TextField
            label="Required ingredient"
            size="small"
            value={requiredIngredient}
            onChange={(e) => setRequiredIngredient(e.target.value)}
            placeholder="e.g. haddock"
            sx={{ minWidth: 180 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={kidFriendly}
                onChange={(e) => setKidFriendly(e.target.checked)}
                size="small"
              />
            }
            label="Kid-friendly"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={quickEasy}
                onChange={(e) => setQuickEasy(e.target.checked)}
                size="small"
              />
            }
            label="Quick & Easy"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isModular}
                onChange={(e) => setIsModular(e.target.checked)}
                size="small"
              />
            }
            label="Modular"
          />

          <TextField
            label="Count"
            type="number"
            size="small"
            value={count}
            onChange={(e) => setCount(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
            slotProps={{ htmlInput: { min: 1, max: 20 } }}
            sx={{ width: 80 }}
          />

          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <GenerateIcon />}
            onClick={handleGenerate}
            disabled={generating || seeding}
          >
            {generating ? 'Generating...' : 'Generate Names'}
          </Button>
        </Stack>
      </Paper>

      {/* Add by Name Panel — for admins who already know what they want */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Recipes by Name
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Skip AI generation — paste recipe names (one per line). Stubs are created immediately; run Batch Enrichment afterward to fill in ingredients & steps.
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            multiline
            minRows={3}
            maxRows={10}
            placeholder={'Peanut butter and jelly sandwich\nMac and cheese\nPasta with marinara sauce'}
            value={byNameInput}
            onChange={(e) => setByNameInput(e.target.value)}
            fullWidth
            size="small"
          />

          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Meal Type</InputLabel>
              <Select
                value={byNameMealType}
                label="Meal Type"
                onChange={(e) => setByNameMealType(e.target.value)}
              >
                <MenuItem value="breakfast">Breakfast</MenuItem>
                <MenuItem value="lunch">Lunch</MenuItem>
                <MenuItem value="dinner">Dinner</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Dietary Tags (optional)</InputLabel>
              <Select
                multiple
                value={byNameTags}
                label="Dietary Tags (optional)"
                onChange={(e) => setByNameTags(e.target.value as string[])}
                renderValue={(sel) => (sel as string[]).join(', ')}
              >
                {DIETARY_TAG_GROUPS.map((group) => [
                  <MenuItem key={group.label} disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', mt: 0.5 }}>
                    {group.label}
                  </MenuItem>,
                  ...group.tags.map((tag) => (
                    <MenuItem key={tag} value={tag} sx={{ pl: 3 }}>
                      <Checkbox checked={byNameTags.includes(tag)} size="small" />
                      {tag}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={seeding ? <CircularProgress size={18} color="inherit" /> : <SeedIcon />}
              onClick={handleAddByName}
              disabled={seeding || generating || byNameInput.trim().length === 0}
            >
              {seeding ? 'Adding...' : 'Add Recipes'}
            </Button>
          </Stack>

          {seeding && (
            <Box>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {seedProgress || 'Processing...'}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Preview Table */}
      {preview.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Preview ({selected.size} of {preview.length} selected)
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button size="small" onClick={toggleAll}>
                {selected.size === preview.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={seeding ? <CircularProgress size={18} color="inherit" /> : <SeedIcon />}
                onClick={handleSeed}
                disabled={seeding || selected.size === 0}
              >
                {seeding ? 'Seeding...' : `Create ${selected.size} Recipes`}
              </Button>
            </Stack>
          </Box>

          {seeding && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {seedProgress || 'Processing...'}
              </Typography>
            </Box>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.size === preview.length}
                      indeterminate={selected.size > 0 && selected.size < preview.length}
                      onChange={toggleAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Total Time</TableCell>
                  <TableCell>Cuisine</TableCell>
                  <TableCell>Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((recipe, i) => (
                  <TableRow key={i} selected={selected.has(i)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(i)}
                        onChange={() => toggleSelected(i)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {recipe.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                        {recipe.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={`${recipe.total_time}m`} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {recipe.cuisine && (
                        <Chip size="small" label={recipe.cuisine} color="secondary" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {recipe.dietary_tags.map((tag) => (
                          <Chip key={tag} size="small" label={tag} color="primary" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Post-seed result */}
      {lastResult && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Seeding Result
          </Typography>
          <Stack direction="row" spacing={2} mb={2}>
            <Chip label={`${lastResult.created} created`} color="success" />
            {(lastResult.skipped || 0) > 0 && (
              <Chip label={`${lastResult.skipped} skipped (duplicates)`} color="warning" />
            )}
            {lastResult.failed > 0 && (
              <Chip label={`${lastResult.failed} failed`} color="error" />
            )}
          </Stack>

          {lastResult.errors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {lastResult.errors.map((err, i) => (
                <Typography key={i} variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {err.name}: {err.error}
                </Typography>
              ))}
            </Box>
          )}

          {lastResult.created > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {lastResult.created} stubs created. Run{' '}
              <Button
                size="small"
                variant="text"
                onClick={() => navigate('/admin/enrichment')}
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
              >
                Batch Enrichment
              </Button>{' '}
              to generate ingredients & steps for these recipes.
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default RecipeSeeding;
